/**
 * DataGrid - Universal AG Grid wrapper component.
 *
 * Handles all AG Grid boilerplate: scroll chevrons, theme switching,
 * sort sync, selection sync, resize observers, and RAF smooth scrolling.
 * Entity-specific logic lives entirely in columnDefs and cell renderers
 * passed in as props.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ColumnState,
  type IRowNode,
  type GridReadyEvent,
  type SortChangedEvent,
  type SelectionChangedEvent,
  type GridApi,
  type GetRowIdParams,
  type Theme,
} from 'ag-grid-community';
import { useTheme } from '@shared/theme/ThemeContext';
import { billTableLightTheme, billTableDarkTheme } from './ag-grid-theme';

let agGridModulesRegistered = false;
function ensureModulesRegistered(): void {
  if (!agGridModulesRegistered) {
    ModuleRegistry.registerModules([AllCommunityModule]);
    agGridModulesRegistered = true;
  }
}

let _suppressRowClickUntil = 0;
export function suppressRowClick() {
  _suppressRowClickUntil = Date.now() + 200;
}

export interface DataGridProps<T = any> {
  data: T[];
  columnDefs: ColDef[];
  defaultColDef?: ColDef;
  gridContext?: any;
  getRowId: (params: GetRowIdParams) => string;
  getRowHeight?: (params: any) => number | undefined | null;

  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  sortFieldMap?: Record<string, string>;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;

  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChanged?: (ids: Set<string>) => void;

  onRowClick?: (data: T) => void;
  onScrollNearEnd?: () => void;
  onGridReady?: (api: GridApi) => void;

  headerHeight?: number;
  emptyMessage?: string;
  wrapperClassName?: string;

  /** Optional pinned bottom rows (e.g. totals / footer). Passed straight to AG Grid. */
  pinnedBottomRowData?: any[];
  /** Override the light-mode theme (defaults to the bill-table purple theme). */
  lightTheme?: Theme;
  /** Override the dark-mode theme (defaults to the bill-table purple theme). */
  darkTheme?: Theme;
}

function DataGrid<T = any>({
  data,
  columnDefs,
  defaultColDef: defaultColDefProp,
  gridContext,
  getRowId,
  getRowHeight,
  sortBy,
  sortDirection,
  sortFieldMap,
  onSort,
  selectable = false,
  selectedIds,
  onSelectionChanged,
  onRowClick,
  onScrollNearEnd,
  onGridReady: onGridReadyProp,
  headerHeight = 42,
  emptyMessage = 'No data found',
  wrapperClassName,
  pinnedBottomRowData,
  lightTheme,
  darkTheme,
}: DataGridProps<T>): JSX.Element {
  ensureModulesRegistered();

  const { theme } = useTheme();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<AgGridReact>(null);
  const scrollAnimRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);
  const suppressSelectionRef = useRef(false);
  const isSyncingSortRef = useRef(false);

  const agTheme = useMemo(
    () =>
      theme === 'dark'
        ? (darkTheme ?? billTableDarkTheme)
        : (lightTheme ?? billTableLightTheme),
    [theme, lightTheme, darkTheme]
  );

  const defaultColDef = useMemo<ColDef>(
    () => defaultColDefProp || {
      resizable: true,
      cellClass: 'ag-data-grid-cell',
      sortingOrder: ['asc', 'desc'],
    },
    [defaultColDefProp]
  );

  // --- Scroll chevrons ---

  const updateScrollState = useCallback(() => {
    if (!wrapperRef.current || isScrollingRef.current) return;
    const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
    if (!viewport) return;
    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);

    const pinnedContainer = wrapperRef.current.querySelector('.ag-pinned-left-header') as HTMLElement;
    if (pinnedContainer) {
      wrapperRef.current.style.setProperty('--ag-chevron-left-offset', `${pinnedContainer.offsetWidth}px`);
    }
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
    if (!viewport) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateScrollState, 100);
    };

    viewport.addEventListener('scroll', handleScroll);
    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, gridApi, data]);

  const startScrolling = useCallback((direction: 'left' | 'right') => {
    isScrollingRef.current = true;
    const pixelsPerFrame = direction === 'left' ? -8 : 8;
    const animate = () => {
      if (!wrapperRef.current) return;
      const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement;
      if (!viewport) return;
      viewport.scrollLeft += pixelsPerFrame;
      scrollAnimRef.current = requestAnimationFrame(animate);
    };
    scrollAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
    isScrollingRef.current = false;
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    return () => {
      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    };
  }, []);

  // --- Sort sync ---

  const reverseSortFieldMap = useMemo(() => {
    if (!sortFieldMap) return {};
    const map: Record<string, string> = {};
    for (const [colId, field] of Object.entries(sortFieldMap)) {
      map[field] = colId;
    }
    return map;
  }, [sortFieldMap]);

  // Apply parent sort state to the grid — only when it actually differs
  useEffect(() => {
    if (!gridApi) return;
    const colId = sortBy ? (reverseSortFieldMap[sortBy] || sortBy) : undefined;
    if (!colId || !sortDirection) return;

    const currentState = gridApi.getColumnState();
    const currentSort = currentState.find((c: ColumnState) => c.sort);
    if (currentSort?.colId === colId && currentSort?.sort === sortDirection) return;

    isSyncingSortRef.current = true;
    gridApi.applyColumnState({
      state: [{ colId, sort: sortDirection }],
      defaultState: { sort: null },
    });
    isSyncingSortRef.current = false;
  }, [gridApi, sortBy, sortDirection, reverseSortFieldMap]);

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    if (!onSort) return;
    if (isSyncingSortRef.current) {
      isSyncingSortRef.current = false;
      return;
    }
    const allColState = event.api.getColumnState();
    const sortModel = allColState
      .filter((col: ColumnState) => col.sort)
      .map((col: ColumnState) => ({ colId: col.colId, sort: col.sort }));

    if (sortModel.length === 0) {
      const colId = sortBy ? (reverseSortFieldMap[sortBy] || sortBy) : undefined;
      if (colId && sortDirection) {
        isSyncingSortRef.current = true;
        event.api.applyColumnState({
          state: [{ colId, sort: sortDirection }],
          defaultState: { sort: null },
        });
        isSyncingSortRef.current = false;
      }
      return;
    }

    const { colId, sort } = sortModel[0];
    if (!colId || !sort) return;
    const parentField = sortFieldMap?.[colId] || colId;

    // Don't fire if the sort already matches props (prevents toggle loops)
    if (parentField === sortBy && sort === sortDirection) return;

    onSort(parentField, sort as 'asc' | 'desc');
  }, [onSort, sortBy, sortDirection, sortFieldMap, reverseSortFieldMap]);

  // --- Selection sync ---

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    if (!onSelectionChanged || suppressSelectionRef.current) return;
    const selectedNodes = event.api.getSelectedNodes();
    const ids = new Set(selectedNodes.map((node: IRowNode) => String(node.data?.id)).filter(id => id && id !== 'undefined'));
    onSelectionChanged(ids);
  }, [onSelectionChanged]);

  useEffect(() => {
    if (!gridApi || !selectedIds) return;
    suppressSelectionRef.current = true;
    gridApi.forEachNode((node: IRowNode) => {
      if (!node.data) return;
      const shouldBeSelected = selectedIds.has(String(node.data.id));
      const isSelected = node.isSelected();
      if (shouldBeSelected !== isSelected) {
        node.setSelected(shouldBeSelected);
      }
    });
    suppressSelectionRef.current = false;
  }, [gridApi, selectedIds]);

  // --- Infinite scroll ---

  const handleBodyScroll = useCallback(() => {
    if (!onScrollNearEnd || !gridRef.current?.api) return;
    const api = gridRef.current.api;
    const lastRow = api.getLastDisplayedRowIndex();
    const totalRows = api.getDisplayedRowCount();
    if (totalRows > 0 && totalRows - lastRow <= 10) {
      onScrollNearEnd();
    }
  }, [onScrollNearEnd]);

  // --- Row click ---

  const handleRowClicked = useCallback((event: any) => {
    if (!onRowClick) return;
    if (Date.now() < _suppressRowClickUntil) return;
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea, [role="checkbox"], [data-no-row-click]')) return;
    onRowClick(event.data);
  }, [onRowClick]);

  // --- Grid ready ---

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    onGridReadyProp?.(params.api);
  }, [onGridReadyProp]);

  // --- Selection config ---

  const rowSelectionConfig = useMemo(() =>
    selectable ? {
      mode: 'multiRow' as const,
      headerCheckbox: true,
      checkboxes: true,
      enableClickSelection: false,
    } : undefined,
  [selectable]);

  const selectionColumnDefConfig = useMemo(() =>
    selectable ? {
      pinned: 'left' as const,
      width: 50,
      maxWidth: 50,
      suppressMovable: true,
      sortable: false,
      headerClass: 'ag-data-grid-header-uppercase',
    } : undefined,
  [selectable]);

  // --- Force cell refresh on data fingerprint change ---

  const dataFingerprint = useMemo(
    () => data.length + ':' + (data[0] as any)?.id,
    [data]
  );

  useEffect(() => {
    if (gridApi && data.length > 0) {
      gridApi.refreshCells({ force: true });
    }
  }, [gridApi, dataFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card p-0 relative">
      {data.length > 0 ? (
        <div
          className={`ag-data-grid-wrapper ${wrapperClassName || ''}`}
          ref={wrapperRef}
        >
          <AgGridReact
            ref={gridRef}
            theme={agTheme}
            context={gridContext}
            rowData={data}
            pinnedBottomRowData={pinnedBottomRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            getRowHeight={getRowHeight}
            suppressCellFocus={true}
            rowSelection={rowSelectionConfig}
            selectionColumnDef={selectionColumnDefConfig}
            onGridReady={handleGridReady}
            onSortChanged={handleSortChanged}
            onSelectionChanged={handleSelectionChanged}
            onBodyScroll={handleBodyScroll}
            onRowClicked={handleRowClicked}
            headerHeight={headerHeight}
            animateRows={false}
            enableCellTextSelection={true}
          />

          <button
            className={`ag-scroll-chevron ag-scroll-chevron-left ${canScrollLeft ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
            onMouseDown={() => startScrolling('left')}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            onTouchStart={() => startScrolling('left')}
            onTouchEnd={stopScrolling}
            aria-label="Scroll table left"
            tabIndex={canScrollLeft ? 0 : -1}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            className={`ag-scroll-chevron ag-scroll-chevron-right ${canScrollRight ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
            onMouseDown={() => startScrolling('right')}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            onTouchStart={() => startScrolling('right')}
            onTouchEnd={stopScrolling}
            aria-label="Scroll table right"
            tabIndex={canScrollRight ? 0 : -1}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-text-tertiary dark:text-text-dark-tertiary">
            {emptyMessage}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataGrid;
